import React, {
  useEffect,
  useState,
} from "react";

import { useTranslation } from "react-i18next";

import {
  Download,
  Eye,
  FileText,
  RefreshCw,
} from "lucide-react";

import axios from "../../axiosConfig";

import "../../styles/CurrentLoadOrderPanel.css";

function formatDate(value, locale) {
  if (!value) {
    return null;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  try {
    return new Intl.DateTimeFormat(locale, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(date);
  } catch {
    return new Intl.DateTimeFormat("pt-PT", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(date);
  }
}

export default function CurrentLoadOrderPanel({
  loadId,
}) {
  const { t, i18n } = useTranslation("edit_load");
  const locale = i18n.resolvedLanguage || i18n.language || "pt";

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [busyOrderId, setBusyOrderId] = useState(null);

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creatingDraft, setCreatingDraft] = useState(false);
  const [issuingOrderId, setIssuingOrderId] = useState(null);
  const [confirmingIssueId, setConfirmingIssueId] = useState(null);
  const [issueConfirmed, setIssueConfirmed] = useState(false);
  const [draftPrice, setDraftPrice] = useState("");
  const [draftPaymentTerms, setDraftPaymentTerms] = useState("");
  const [createError, setCreateError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    const numericLoadId = Number(loadId);

    setOrders([]);
    setError("");
    setActionError("");
    setLoading(true);

    setShowCreateForm(false);
    setCreateError("");
    setConfirmingIssueId(null);
    setIssueConfirmed(false);

    if (
      !Number.isSafeInteger(numericLoadId) ||
      numericLoadId <= 0
    ) {
      setError(t("loadOrders.errors.invalidLoadId"));
      setLoading(false);

      return () => controller.abort();
    }

    async function fetchOrders() {
      try {
        const response = await axios.get(
          `/load-orders/by-load/${numericLoadId}`,
          {
            signal: controller.signal,
          }
        );

        if (controller.signal.aborted) {
          return;
        }

        const items = response.data?.loadOrders;

        if (
          response.data?.ok !== true ||
          !Array.isArray(items)
        ) {
          throw new Error("Invalid LoadOrder response");
        }

        setOrders(items);
      } catch (requestError) {
        if (controller.signal.aborted) {
          return;
        }

        console.error(
          "[CurrentLoadOrderPanel] failed to load orders:",
          requestError
        );

        setError(t("loadOrders.errors.fetchFailed"));
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }

    void fetchOrders();

    return () => {
      controller.abort();
    };
  }, [loadId, refreshKey, t]);

  async function handleCreateDraft(event) {
    event.preventDefault();

    if (creatingDraft) return;

    const numericLoadId = Number(loadId);

    if (
      !Number.isSafeInteger(numericLoadId) ||
      numericLoadId <= 0
    ) {
      setCreateError("Identificador da carga inválido.");
      return;
    }

    if (
      orders.some(
        (order) =>
          String(order.status).toUpperCase() === "DRAFT"
      )
    ) {
      setCreateError(
        "Já existe um rascunho para esta carga."
      );
      return;
    }

    const priceText = draftPrice.trim().replace(/\s/g, "");
    const paymentText = draftPaymentTerms.trim();

    const payload = {
      loadId: numericLoadId,
      currency: "EUR",
    };

    if (priceText !== "") {
      if (!/^\d+(?:[.,]\d{1,2})?$/.test(priceText)) {
        setCreateError(
          "Introduz um preço válido, por exemplo 450,00."
        );
        return;
      }

      payload.price = Number(priceText.replace(",", "."));
    }

    if (paymentText !== "") {
      const days = Number(paymentText);

      if (
        !/^\d+$/.test(paymentText) ||
        !Number.isInteger(days) ||
        days < 0 ||
        days > 3650
      ) {
        setCreateError(
          "O prazo de pagamento deve ser entre 0 e 3650 dias."
        );
        return;
      }

      payload.paymentTermsDays = days;
    }

    setCreatingDraft(true);
    setCreateError("");
    setActionError("");

    try {
      const response = await axios.post(
        "/load-orders",
        payload
      );

      if (
        response.data?.ok !== true ||
        !response.data?.loadOrder?.id
      ) {
        throw new Error("Invalid LoadOrder response");
      }

      setShowCreateForm(false);
      setDraftPrice("");
      setDraftPaymentTerms("");

      setRefreshKey((value) => value + 1);
    } catch (error) {
      console.error(
        "[CurrentLoadOrderPanel] create draft failed:",
        error
      );

      setCreateError(
        error.response?.data?.error ||
          "Não foi possível preparar a Ordem de Carga."
      );
    } finally {
      setCreatingDraft(false);
    }
  }

    async function handleIssue(order) {
    const orderId = Number(order?.id);

    if (
      !Number.isSafeInteger(orderId) ||
      orderId <= 0 ||
      String(order?.status).toUpperCase() !== "DRAFT" ||
      !issueConfirmed ||
      confirmingIssueId !== order.id ||
      issuingOrderId !== null
    ) {
      return;
    }

    setIssuingOrderId(order.id);
    setActionError("");

    try {
      const response = await axios.post(
        `/load-orders/${orderId}/issue`,
        {}
      );

      if (
        response.data?.ok !== true ||
        String(response.data?.loadOrder?.status).toUpperCase() !==
          "ISSUED"
      ) {
        throw new Error("Invalid issuance response");
      }

      setConfirmingIssueId(null);
      setIssueConfirmed(false);
      setRefreshKey((value) => value + 1);
    } catch (error) {
      console.error(
        "[CurrentLoadOrderPanel] issue failed:",
        error
      );

      setActionError(
        error.response?.data?.error ||
          "Não foi possível emitir a Ordem de Carga."
      );
    } finally {
      setIssuingOrderId(null);
    }
  }

  async function handlePdf(order, mode) {
    if (
      order?.status !== "ISSUED" ||
      !Number.isSafeInteger(Number(order?.id)) ||
      Number(order.id) <= 0
    ) {
      return;
    }

    setActionError("");

    const previewWindow =
      mode === "view"
        ? window.open("about:blank", "_blank")
        : null;

    if (mode === "view" && !previewWindow) {
      setActionError(t("loadOrders.errors.popupBlocked"));
      return;
    }

    if (previewWindow) {
      previewWindow.opener = null;
      previewWindow.document.body.textContent =
        t("loadOrders.preparingPdf");
    }

    setBusyOrderId(order.id);

    try {
      const response = await axios.get(
        `/load-orders/${order.id}/pdf`,
        {
          responseType: "blob",
        }
      );

      const pdf = new Blob(
        [response.data],
        {
          type: "application/pdf",
        }
      );

      if (pdf.size === 0) {
        throw new Error("Empty PDF");
      }

      const objectUrl = URL.createObjectURL(pdf);

      if (mode === "view") {
        previewWindow.location.replace(objectUrl);
      } else {
        const link = document.createElement("a");

        link.href = objectUrl;
        link.download = `LOADEvo-OC-${order.id}.pdf`;

        document.body.appendChild(link);
        link.click();
        link.remove();
      }

      window.setTimeout(() => {
        URL.revokeObjectURL(objectUrl);
      }, 120000);
    } catch (requestError) {
      if (previewWindow && !previewWindow.closed) {
        previewWindow.close();
      }

      console.error(
        "[CurrentLoadOrderPanel] PDF request failed:",
        requestError
      );

      setActionError(t("loadOrders.errors.pdfFailed"));
    } finally {
      setBusyOrderId(null);
    }
  }

  return (
    <section className="current-load-order-panel">
      <header className="current-load-order-panel__header">
        <div className="current-load-order-panel__heading">
          <span className="current-load-order-panel__icon">
            <FileText size={18} />
          </span>

          <div>
            <h2>{t("loadOrders.title")}</h2>

            <p>{t("loadOrders.subtitle")}</p>
          </div>
        </div>

        <div className="current-load-order-panel__header-actions">
          {!loading &&
            !error &&
            !orders.some(
              (order) =>
                String(order.status).toUpperCase() === "DRAFT"
            ) && (
              <button
                type="button"
                className="current-load-order-panel__create-button"
                onClick={() => {
                  setShowCreateForm((value) => !value);
                  setCreateError("");
                }}
                disabled={creatingDraft}
              >
                {showCreateForm
                  ? "Fechar preparação"
                  : "+ Nova Ordem de Carga"}
              </button>
            )}

          <button
            type="button"
            className="current-load-order-panel__refresh"
            onClick={() => setRefreshKey((value) => value + 1)}
            disabled={
              loading ||
              creatingDraft ||
              issuingOrderId !== null
            }
          >
            <RefreshCw size={14} />
            {t("loadOrders.actions.refresh")}
          </button>
        </div>
      </header>

      <div className="current-load-order-panel__body">
        {showCreateForm &&
          !loading &&
          !error &&
          !orders.some(
            (order) =>
              String(order.status).toUpperCase() === "DRAFT"
          ) && (
            <form
              className="current-load-order-panel__create-form"
              onSubmit={handleCreateDraft}
            >
              <div className="current-load-order-panel__create-heading">
                <strong>Preparar Ordem de Carga</strong>
                <span>
                  Revê as condições comerciais antes de criar
                  o rascunho.
                </span>
              </div>

              <div className="current-load-order-panel__create-fields">
                <label>
                  Preço de subcontratação
                  <div className="current-load-order-panel__input-wrap">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={draftPrice}
                      onChange={(event) =>
                        setDraftPrice(event.target.value)
                      }
                      placeholder="Valor da carga"
                      disabled={creatingDraft}
                    />
                    <span>EUR</span>
                  </div>
                  <small>
                    Deixa vazio para utilizar o valor
                    registado na carga.
                  </small>
                </label>

                <label>
                  Prazo de pagamento
                  <div className="current-load-order-panel__input-wrap">
                    <input
                      type="number"
                      min="0"
                      max="3650"
                      step="1"
                      value={draftPaymentTerms}
                      onChange={(event) =>
                        setDraftPaymentTerms(event.target.value)
                      }
                      placeholder="Não especificado"
                      disabled={creatingDraft}
                    />
                    <span>dias</span>
                  </div>
                  <small>
                    Opcional. Indica o prazo acordado
                    com o transportador.
                  </small>
                </label>
              </div>

              {createError && (
                <div
                  className="current-load-order-panel__message is-error"
                  role="alert"
                >
                  {createError}
                </div>
              )}

              <div className="current-load-order-panel__create-footer">
                <span>
                  Esta ação cria apenas um rascunho.
                  Não emite o documento.
                </span>

                <button
                  type="submit"
                  disabled={creatingDraft}
                >
                  {creatingDraft
                    ? "A preparar..."
                    : "Preparar rascunho"}
                </button>
              </div>
            </form>
          )}

        {loading ? (
          <div className="current-load-order-panel__empty">
            {t("loadOrders.loading")}
          </div>
        ) : error ? (
          <div
            className="current-load-order-panel__message is-error"
            role="alert"
          >
            {error}
          </div>
        ) : orders.length === 0 ? (
          <div className="current-load-order-panel__empty">
            <FileText size={22} />

            <strong>{t("loadOrders.empty.title")}</strong>

            <span>{t("loadOrders.empty.description")}</span>
          </div>
        ) : (
          <div className="current-load-order-panel__list">
            {orders.map((order) => {
              const status = String(
                order.status || ""
              ).toUpperCase();

              const statusKey =
                [
                  "DRAFT",
                  "ISSUED",
                  "SENT",
                  "ACCEPTED",
                  "REJECTED",
                  "CANCELLED",
                ].includes(status)
                  ? `loadOrders.status.${status}`
                  : "loadOrders.status.UNKNOWN";

              const canOpenPdf = status === "ISSUED";
              const isBusy = busyOrderId === order.id;
              const issuedDate = formatDate(order.issuedAt, locale);

              return (
                <article
                  key={order.id}
                  className="current-load-order-panel__item"
                >
                  <div className="current-load-order-panel__document">
                    <span className="current-load-order-panel__document-icon">
                      <FileText size={17} />
                    </span>

                    <div className="current-load-order-panel__document-info">
                      <strong>
                        {order.documentNumber ||
                          t("loadOrders.documentFallback", {
                            id: order.id,
                          })}
                      </strong>

                      <span>
                        {issuedDate
                          ? t("loadOrders.issuedOn", {
                              date: issuedDate,
                            })
                          : t("loadOrders.createdDocument", {
                              id: order.id,
                            })}
                      </span>
                    </div>
                  </div>

                  <span
                    className={`current-load-order-panel__status is-${status.toLowerCase()}`}
                  >
                    {t(statusKey)}
                  </span>

                  <div className="current-load-order-panel__actions">
                    {canOpenPdf ? (
                      <>
                        <button
                          type="button"
                          onClick={() => handlePdf(order, "view")}
                          disabled={busyOrderId !== null}
                        >
                          <Eye size={14} />
                          {isBusy
                            ? t("loadOrders.actions.preparing")
                            : t("loadOrders.actions.view")}
                        </button>

                        <button
                          type="button"
                          onClick={() => handlePdf(order, "download")}
                          disabled={busyOrderId !== null}
                        >
                          <Download size={14} />
                          {t("loadOrders.actions.download")}
                        </button>
                      </>
                    ) : status === "DRAFT" ? (
                      <button
                        type="button"
                        disabled={issuingOrderId !== null}
                        onClick={() => {
                          setConfirmingIssueId(
                            confirmingIssueId === order.id
                              ? null
                              : order.id
                          );
                          setIssueConfirmed(false);
                          setActionError("");
                        }}
                      >
                        {confirmingIssueId === order.id
                          ? "Fechar revisão"
                          : "Rever e emitir"}
                      </button>
                    ) : (
                      <span className="current-load-order-panel__unavailable">
                        {t("loadOrders.pdfUnavailable")}
                      </span>
                    )}
                  </div>

                  {status === "DRAFT" &&
                    confirmingIssueId === order.id && (
                      <div className="current-load-order-panel__issue-review">
                        <strong>Confirmar emissão da Ordem de Carga</strong>

                        <div className="current-load-order-panel__issue-details">
                          <span>Preço de subcontratação</span>
                          <strong>
                            {order.price == null
                              ? "Não especificado"
                              : new Intl.NumberFormat("pt-PT", {
                                  style: "currency",
                                  currency:
                                    order.currency || "EUR",
                                }).format(Number(order.price))}
                          </strong>

                          <span>Prazo de pagamento</span>
                          <strong>
                            {order.paymentTermsDays == null
                              ? "Não especificado"
                              : `${order.paymentTermsDays} dias`}
                          </strong>
                        </div>

                        <p>
                          Ao confirmar, será atribuído um número
                          oficial à Ordem de Carga. Os dados da
                          carga, dos locais e do transportador
                          serão congelados no documento.
                        </p>

                        <label className="current-load-order-panel__issue-confirm">
                          <input
                            type="checkbox"
                            checked={issueConfirmed}
                            disabled={issuingOrderId !== null}
                            onChange={(event) =>
                              setIssueConfirmed(event.target.checked)
                            }
                          />
                          <span>
                            Confirmei as condições comerciais e
                            pretendo emitir este documento.
                          </span>
                        </label>

                        <button
                          type="button"
                          className="current-load-order-panel__issue-button"
                          disabled={
                            !issueConfirmed ||
                            issuingOrderId !== null
                          }
                          onClick={() => handleIssue(order)}
                        >
                          {issuingOrderId === order.id
                            ? "A emitir..."
                            : "Confirmar emissão"}
                        </button>
                      </div>
                    )}
                </article>
              );
            })}
          </div>
        )}

        {actionError && (
          <div
            className="current-load-order-panel__message is-error"
            role="alert"
          >
            {actionError}
          </div>
        )}
      </div>
    </section>
  );
}
